import React, { useState } from 'react';
import VideoUploadButton from '../../../components/ui/VideoUploadButton';
import Icon from '../../../components/AppIcon';

const LevelThreeSection = ({ data, onChange, errors, songId, talentId }) => {
  const [uploadedVideos, setUploadedVideos] = useState({
    opening: [],
    middle: [],
    ending: []
  });
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  const handleSubsectionChange = (subsection, field, value) => {
    onChange({
      ...data,
      [subsection]: {
        ...data?.[subsection],
        [field]: value
      }
    });
  };

  const handleVideoUploadSuccess = (uploadData, subsection) => {
    setUploadedVideos(prev => ({
      ...prev,
      [subsection]: [...prev?.[subsection], uploadData]
    }));
    setUploadSuccess(`Video uploaded successfully for ${subsection}: ${uploadData?.fileName}`);
    setUploadError('');
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setUploadSuccess('');
    }, 3000);
  };

  const handleVideoUploadError = (error) => {
    setUploadError(error);
    setUploadSuccess('');
  };

  const subsections = [
    { key: 'opening', title: 'Opening' },
    { key: 'middle', title: 'Middle' },
    { key: 'ending', title: 'Ending' }
  ];

  return (
    <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
      {/* Section Header */}
      <h2 className="text-2xl font-heading font-bold text-white mb-6">Level 3: Audition Prep</h2>
      
      {/* Upload Messages */}
      {uploadError && (
        <div className="mb-4 bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-3">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-red-800" />
            <p className="text-red-800 text-sm">{uploadError}</p>
          </div>
        </div>
      )}

      {uploadSuccess && (
        <div className="mb-4 bg-green-50 bg-opacity-90 border border-green-200 rounded-xl p-3">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} className="text-green-800" />
            <p className="text-green-800 text-sm">{uploadSuccess}</p>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {subsections?.map((subsection) => (
          <div key={subsection?.key} className="border border-white border-opacity-20 rounded-xl p-4">
            {/* Subsection Header with Video Upload */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <h3 className="text-xl font-heading font-semibold text-white">{subsection?.title}</h3>
              
              <VideoUploadButton
                level="level3"
                subsection={subsection?.key}
                songId={songId}
                talentId={talentId}
                onUploadSuccess={(uploadData) => handleVideoUploadSuccess(uploadData, subsection?.key)}
                onUploadError={handleVideoUploadError}
                size="sm"
                className="shrink-0"
              />
            </div>

            {/* Uploaded Videos for this subsection */}
            {uploadedVideos?.[subsection?.key]?.length > 0 && (
              <div className="mb-4 bg-blue-50 bg-opacity-90 border border-blue-200 rounded-xl p-3">
                <h4 className="text-blue-800 font-medium mb-2 text-sm">
                  {subsection?.title} Videos ({uploadedVideos?.[subsection?.key]?.length})
                </h4>
                <div className="space-y-1">
                  {uploadedVideos?.[subsection?.key]?.map((video, index) => (
                    <div key={index} className="flex items-center justify-between text-xs text-blue-700 bg-white bg-opacity-50 rounded-lg p-2">
                      <div className="flex items-center space-x-2">
                        <Icon name="Video" size={12} />
                        <span>{video?.fileName}</span>
                      </div>
                      <span className="text-xs">
                        {(video?.fileSize / (1024 * 1024))?.toFixed(1)} MB
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4">
              {/* Reference Lyric Line - Single line */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Reference Lyric Line
                </label>
                <input
                  type="text"
                  value={data?.[subsection?.key]?.referenceLyricLine || ''}
                  onChange={(e) => handleSubsectionChange(subsection?.key, 'referenceLyricLine', e?.target?.value)}
                  placeholder={`Enter the reference lyric line for the ${subsection?.key}`}
                  className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
              </div>
              
              {/* Description - Paragraph */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  How to sing and act this part of the song
                </label>
                <textarea
                  value={data?.[subsection?.key]?.description || ''}
                  onChange={(e) => handleSubsectionChange(subsection?.key, 'description', e?.target?.value)}
                  placeholder={`Describe how to sing and act the ${subsection?.key} part of the song`}
                  rows={4}
                  className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LevelThreeSection;